import { Option } from 'app/types/interfaces/Option';
import React, { useEffect } from 'react';
import { Dropdown, Space, Button, List, Input, Checkbox } from 'antd';
import { SearchOutlined, DownOutlined } from '@ant-design/icons';
import { isString } from 'lodash';
import { withFetchResource } from 'app/HOCs/withFetchResource';
import classes from './Styles/FilterDropdown.module.scss';
import cn from 'classnames';

type RenderLabel = (options: {
  selected: Array<string>;
}) => string | React.ReactNode;

export type FilterDropdownProps = {
  value: string[];
  options?: Option[];
  onApply?: (value: string[]) => void;
  onReset?: (value: string[]) => void;
  allOption?: {
    value: 'all';
    label?: string | React.ReactNode;
  };
  footer?: () => React.ReactNode;
  searchProps?: {
    placeholder?: string;
    prefix?: React.ReactNode;
  };
  showSearch?: boolean;
  renderLabel: RenderLabel;
  trigger?: ('click' | 'hover' | 'contextMenu')[];
  loading?: boolean;
  icon?: React.ReactNode;
};

const FilterDropdown = (props: FilterDropdownProps) => {
  const {
    value,
    options,
    onApply,
    onReset,
    allOption,
    // footer,
    // searchProps,
    showSearch,
    renderLabel,
    loading,
    icon,
    trigger = ['click']
  } = props;
  const [selectedAll, setSelectedAll] = React.useState(true);
  const [keywords, setKeywords] = React.useState('');
  const [open, setOpen] = React.useState(false);
  const [selectedValues, setSelectedValues] = React.useState([]);

  const combinedOptionsWithSelectedValue = React.useMemo(() => {
    return options.map((option) => ({
      ...option,
      isSelected: selectedValues.includes(option.value) || selectedAll
    }));
  }, [options, selectedValues, selectedAll]);

  const handleApply = () => {
    onApply(selectedValues);
    setOpen(false);
  };

  const handleReset = () => {
    setSelectedValues([]);
    onReset([]);
  };

  const handleChangeKeyword = (e: React.ChangeEvent<HTMLInputElement>) => {
    setKeywords(e.target.value);
  };

  const handleSelect = (option: Option) => (event: any) => {
    const newSelectedValues = combinedOptionsWithSelectedValue
      .map((item) => {
        if (item.value === option.value) {
          return {
            ...item,
            isSelected: event.target.checked
          };
        }
        return item;
      })
      .filter(({ isSelected }) => isSelected);

    if (newSelectedValues.length === options.length) {
      setSelectedValues([]);
    } else {
      setSelectedValues(newSelectedValues.map(({ value }) => value));
    }
    setTimeout(() => {
      setSelectedAll(newSelectedValues.length === options.length);
    });
  };

  const handleSelectAll = (event) => {
    setSelectedAll(event.target.checked);
    setSelectedValues([]);
  };

  const optionItems = React.useMemo(() => {
    const checkboxOptions = combinedOptionsWithSelectedValue
      .filter((item) => {
        if (!keywords) {
          return true;
        }
        if (!isString(item.label)) {
          return true;
        }
        return item.label.toLowerCase().includes(keywords.toLowerCase());
      })
      .map((item) => {
        return {
          key: item.value,
          label: (
            <Checkbox onChange={handleSelect(item)} checked={item.isSelected}>
              {item.label}
            </Checkbox>
          )
        };
      });
    if (allOption) {
      checkboxOptions.unshift({
        key: allOption.value,
        label: (
          <Checkbox
            onChange={handleSelectAll}
            checked={selectedAll}
            indeterminate={
              selectedValues.length > 0 &&
              selectedValues.length < options.length
            }
          >
            {allOption.label}
          </Checkbox>
        )
      });
    }
    return checkboxOptions;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [combinedOptionsWithSelectedValue, allOption, selectedAll, keywords]);

  useEffect(() => {
    setSelectedAll(!value || !value.length);
    setSelectedValues(value || []);
  }, [value]);

  useEffect(() => {
    if (!open) {
      // Reset selected values when dropdown is closed
      setSelectedAll(!value || !value.length);
      setSelectedValues(value || []);
    }
  }, [open]);

  return (
    <Dropdown
      className={classes.FilterDropdown}
      trigger={trigger}
      open={open}
      menu={{ items: optionItems }}
      onOpenChange={(isOpen) => {
        setOpen(isOpen);
      }}
      dropdownRender={(menuItems) => {
        return (
          <List
            className={classes.filterDropdownList}
            loading={loading}
            header={
              showSearch && (
                <Input
                  onChange={handleChangeKeyword}
                  prefix={<SearchOutlined></SearchOutlined>}
                />
              )
            }
            dataSource={React.Children.toArray(menuItems)}
            renderItem={(menu) => menu}
            footer={
              <Space>
                <Button loading={loading} type="primary" onClick={handleApply}>
                  Apply
                </Button>
                <Button loading={loading} onClick={handleReset}>
                  Reset
                </Button>
              </Space>
            }
          ></List>
        );
      }}
    >
      <Space size={8}>
        <Button
          className={cn(classes.dropdownButton, {
            [classes.active]: !!selectedValues?.length
          })}
          type="text"
          icon={icon}
          onClick={(e) => {
            e.preventDefault();
            setOpen(true);
          }}
          style={{ color: '#3A4861' }}
        >
          <Space size={8}>
            {renderLabel({ selected: selectedValues })}
            <DownOutlined />
          </Space>
        </Button>
      </Space>
    </Dropdown>
  );
};

export const SimpleFilterDropdown = FilterDropdown;

export const AsyncFilterDropdown =
  withFetchResource<FilterDropdownProps>(FilterDropdown);
